import React from 'react';
import axios from "axios";
import RouteMap from './RouteMap';
import './ResultsPanel.css';

const TrophyIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4Z" /><path d="M5 4H3v2a4 4 0 0 0 4 4M19 4h2v2a4 4 0 0 1-4 4" />
    </svg>
);
const FuelIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 22V7a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v15" /><path d="M13 9h3l3 3v6a1 1 0 0 1-1 1h-1" /><circle cx="17.5" cy="17.5" r="1.5" /><path d="M3 22h10" />
    </svg>
);
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 6 9 17l-5-5" />
    </svg>
);

function ResultsPanel({ results }) {
    if (!results || !results.recommendedStations) return null;
    const { savedRequest, recommendedStations } = results;

    const handleOpenNavigation = (station, index) => {
        const token = localStorage.getItem('tankly_token');
        const rawUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
        let baseUrl = rawUrl.trim();
        if (!baseUrl.startsWith('http')) {
            baseUrl = `https://${baseUrl}`;
        }
        axios.put(`${baseUrl}/api/routes/${savedRequest.id}/select/${index}`,
            {},
            { headers: { 'Authorization': `Bearer ${token}` } }
        ).catch(err => console.error('Could not track station selection', err));

        const start = savedRequest.fromLocation;
        const endCoords = savedRequest.toLocation;
        const pumpCoords = `${station.latitude},${station.longitude}`;
        
        let url = 'https://www.google.com/maps/dir/?api=1';
        if (endCoords && endCoords.trim() !== '') {
            url += `&origin=${encodeURIComponent(start)}&destination=${encodeURIComponent(endCoords)}&waypoints=${encodeURIComponent(pumpCoords)}`;
        } else {
            url += `&origin=${encodeURIComponent(start)}&destination=${encodeURIComponent(pumpCoords)}`;
        }

        window.open(url, '_blank', 'noopener,noreferrer');
    };

    if (recommendedStations.length === 0) {
        return (
            <section className="results-panel">
                <h2>No stop needed</h2>
                <p>You have enough fuel to reach {savedRequest.toLocation} without a detour.</p>
            </section>
        );
    }

    const minFuelPrice = Math.min(...recommendedStations.map(s => s.fuelPrice));
    const minTotalCost = Math.min(...recommendedStations.map(s => s.totalCost));

    return (
        <section className="results-panel" aria-labelledby="results-heading">
            <h2 id="results-heading">Best stations along your route</h2>
            <p className="results-route">{savedRequest.fromLocation} → {savedRequest.toLocation}</p>
            <RouteMap stations={recommendedStations} />

            <ul className="station-list">
                {recommendedStations.map((station, index) => {
                    const isCheapest = station.fuelPrice === minFuelPrice;
                    const isBestValue = station.totalCost === minTotalCost;
                    return (
                        <li key={index}>
                            <article className="station-card">
                                <div className="station-badges">
                                    {isBestValue && <span className="badge badge-value"><TrophyIcon /> Best value</span>}
                                    {isCheapest && <span className="badge badge-cheap"><FuelIcon /> Cheapest fuel</span>}
                                </div>

                                <h3>#{index + 1} {station.stationName}</h3>
                                <p className="station-address">{station.stationAddress}</p>

                                <dl className="station-stats">
                                    <div><dt>Price</dt><dd className="mono">€{station.fuelPrice.toFixed(3)}/L</dd></div>
                                    <div><dt>Detour</dt><dd className="mono">{station.detourTimeMinutes} min</dd></div>
                                    <div><dt>Off route</dt><dd className="mono">{(station.distanceFromRoute / 1000).toFixed(1)} km</dd></div>
                                </dl>

                                {station.savingsAmount > 0 && (
                                    <p className="savings-callout"><CheckIcon /> You save €{station.savingsAmount.toFixed(2)} vs. average</p>
                                )}

                                <div className="cost-breakdown" style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'var(--surface-variant, #f3f4f6)', borderRadius: '6px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem' }}>
                                        <strong>Total stop cost:</strong>
                                        <strong className="mono">€{station.actualPumpCost?.toFixed(2) || station.totalCost.toFixed(2)}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #6b7280)', marginTop: '0.25rem', textAlign: 'right' }}>
                                        *(Final price you pay at the pump)*
                                    </div>
                                </div>

                                <button type="button" className="btn-primary nav-button" onClick={() => handleOpenNavigation(station, index)} style={{ marginTop: '1rem' }}>
                                    Open in Google Maps
                                </button>
                            </article>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

export default ResultsPanel;
